/* ============================================
   NODUS.JS - Main JavaScript File
   All custom JavaScript consolidated here
   ============================================ */

if (!customElements.get('nodus-description-blur')) {
  class NodusDescriptionBlur extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-description-content]');
      this.toggle = this.querySelector('[data-description-toggle]');
      this.labelMore = this.querySelector('[data-label-more]');
      this.labelLess = this.querySelector('[data-label-less]');

      if (!this.content || !this.toggle) return;

      this.#releaseInnerScroll();

      this.boundToggle = this.#onToggle.bind(this);
      this.toggle.addEventListener('click', this.boundToggle);

      this.#checkOverflow();

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.#checkOverflow());
        this.resizeObserver.observe(this.content);
      }
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.boundToggle);
      this.resizeObserver?.disconnect();
    }

    #releaseInnerScroll() {
      if (!this.closest('.collection-header')) return;

      let lockedHeight = 0;

      for (const node of this.content.querySelectorAll('[style]')) {
        const clips = node.scrollHeight > node.clientHeight + 2;

        if (clips && node.clientHeight > lockedHeight) lockedHeight = node.clientHeight;

        node.style.overflow = 'visible';
        node.style.maxHeight = 'none';

        if (node.style.height) node.style.height = 'auto';
      }

      if (!lockedHeight) return;

      this.lockedHeight = lockedHeight;
      this.content.style.maxHeight = `${lockedHeight}px`;
    }

    #checkOverflow() {
      if (this.classList.contains('is-expanded')) return;

      const isOverflowing = this.content.scrollHeight > this.content.clientHeight + 2;

      this.classList.toggle('is-short', !isOverflowing);
    }

    #onToggle() {
      const isExpanded = this.classList.toggle('is-expanded');

      this.toggle.setAttribute('aria-expanded', String(isExpanded));
      this.labelMore.hidden = isExpanded;
      this.labelLess.hidden = !isExpanded;

      if (this.lockedHeight) {
        this.content.style.maxHeight = isExpanded ? 'none' : `${this.lockedHeight}px`;
      }

      if (!isExpanded) {
        requestAnimationFrame(() => this.#checkOverflow());
      }
    }
  }

  customElements.define('nodus-description-blur', NodusDescriptionBlur);
}

if (!customElements.get('nodus-buttons')) {
  class NodusButtons extends HTMLElement {
    connectedCallback() {
      this.#setupSliderDrag();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    #setupSliderDrag() {
      if (!this.classList.contains('nodus-buttons__list--mobile-slider')) return;
      if (window.matchMedia('(min-width: 700px)').matches) return;
      if (!window.matchMedia('(pointer: fine)').matches) return;

      this.boundPointerDown = this.#onPointerDown.bind(this);
      this.boundPointerMove = this.#onPointerMove.bind(this);
      this.boundPointerUp = this.#onPointerUp.bind(this);

      this.addEventListener('pointerdown', this.boundPointerDown);
    }

    #onPointerDown(event) {
      if (event.pointerType === 'touch') return;

      this.isDragging = true;
      this.startX = event.clientX;
      this.scrollStart = this.scrollLeft;
      this.setPointerCapture?.(event.pointerId);

      this.addEventListener('pointermove', this.boundPointerMove);
      this.addEventListener('pointerup', this.boundPointerUp);
      this.addEventListener('pointercancel', this.boundPointerUp);
    }

    #onPointerMove(event) {
      if (!this.isDragging) return;

      const delta = event.clientX - this.startX;

      this.scrollLeft = this.scrollStart - delta;
    }

    #onPointerUp() {
      this.isDragging = false;
      this.removeEventListener('pointermove', this.boundPointerMove);
      this.removeEventListener('pointerup', this.boundPointerUp);
      this.removeEventListener('pointercancel', this.boundPointerUp);
    }

    cleanup() {
      this.removeEventListener('pointerdown', this.boundPointerDown);
      this.removeEventListener('pointermove', this.boundPointerMove);
      this.removeEventListener('pointerup', this.boundPointerUp);
      this.removeEventListener('pointercancel', this.boundPointerUp);
    }
  }

  customElements.define('nodus-buttons', NodusButtons);
}

if (!customElements.get('nodus-shipping-cutoff')) {
  class NodusShippingCutoff extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-shipping-cutoff-text]');

      if (!this.messageEl) return;

      this.cutoffHour = Number(this.dataset.cutoffHour || 13);
      this.timezone = this.dataset.timezone || 'Europe/Istanbul';
      this.sameDayMessage = this.dataset.sameDay || '';
      this.nextDayMessage = this.dataset.nextDay || '';

      this.#updateMessage();
      this.intervalId = setInterval(() => this.#updateMessage(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.intervalId);
    }

    #getLocalTime() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(new Date());

      const time = { hour: 0, minute: 0 };

      for (const part of parts) {
        if (part.type === 'hour' || part.type === 'minute') {
          time[part.type] = Number(part.value);
        }
      }

      if (time.hour === 24) {
        time.hour = 0;
      }

      return time;
    }

    #updateMessage() {
      const { hour, minute } = this.#getLocalTime();
      const currentMinutes = hour * 60 + minute;
      const cutoffMinutes = this.cutoffHour * 60;
      const isSameDay = currentMinutes <= cutoffMinutes;

      this.messageEl.textContent = isSameDay ? this.sameDayMessage : this.nextDayMessage;
    }
  }

  customElements.define('nodus-shipping-cutoff', NodusShippingCutoff);
}

if (!customElements.get('nodus-lp-carousel')) {
  class NodusLpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lp-track]');
      this.prevButton = this.querySelector('[data-lp-prev]');
      this.nextButton = this.querySelector('[data-lp-next]');
      this.autoplayDelay = Number(this.dataset.lpAutoplay || 0);

      if (!this.track) return;

      this.onPrev = () => this.scrollTrack(-1);
      this.onNext = () => this.scrollTrack(1);
      this.onPause = () => this.pauseAutoplay();
      this.onResume = () => this.startAutoplay();

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.addEventListener('mouseenter', this.onPause);
      this.addEventListener('mouseleave', this.onResume);
      this.addEventListener('focusin', this.onPause);
      this.addEventListener('focusout', this.onResume);

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.removeEventListener('mouseenter', this.onPause);
      this.removeEventListener('mouseleave', this.onResume);
      this.removeEventListener('focusin', this.onPause);
      this.removeEventListener('focusout', this.onResume);
    }

    scrollTrack(direction) {
      const item = this.track.querySelector('[data-lp-item]');
      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 12;
      const amount = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      if (direction > 0 && this.track.scrollLeft >= maxScroll - 4) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    pauseAutoplay() {
      clearInterval(this.autoplayId);
      this.autoplayId = null;
    }

    startAutoplay() {
      if (this.autoplayDelay <= 0) return;

      this.pauseAutoplay();
      this.autoplayId = setInterval(() => this.scrollTrack(1), this.autoplayDelay);
    }
  }

  customElements.define('nodus-lp-carousel', NodusLpCarousel);
}

if (!customElements.get('nodus-lp-cards')) {
  class NodusLpCards extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-lp-row]'));

      if (!this.rows.length) return;

      this.rowState = new Map();
      this.onScrollCheck = this.onScrollCheck.bind(this);
      this.onScroll = this.onScroll.bind(this);

      for (const row of this.rows) {
        this.rowState.set(row, false);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.onScrollCheck(), {
          threshold: [0, 0.2, 0.5, 0.8]
        });

        for (const row of this.rows) {
          this.observer.observe(row);
        }
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('touchmove', this.onScroll, { passive: true });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.onScrollCheck());
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('touchmove', this.onScroll);
    }

    onScroll() {
      if (this.scrollTick) return;

      this.scrollTick = true;
      requestAnimationFrame(() => {
        this.scrollTick = false;
        this.onScrollCheck();
      });
    }

    onScrollCheck() {
      for (const row of this.rows) {
        const wasVisible = this.rowState.get(row);
        const visible = this.isRowInView(row, wasVisible);

        if (wasVisible === visible) continue;

        this.rowState.set(row, visible);

        for (const item of row.querySelectorAll('.nodus-lp-cards__item')) {
          item.classList.toggle('is-in', visible);
        }
      }
    }

    isRowInView(row, wasVisible) {
      const rect = row.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;

      if (wasVisible) {
        return rect.top < viewport * 0.9 && rect.bottom > viewport * 0.1;
      }

      return rect.top < viewport * 0.78 && rect.bottom > viewport * 0.18;
    }
  }

  customElements.define('nodus-lp-cards', NodusLpCards);
}

if (!customElements.get('nodus-kingpro-anim')) {
  class NodusKingproAnim extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(this.querySelectorAll('[data-kingpro-group]'));
      this.groupState = new Map();
      this.onScrollCheck = this.onScrollCheck.bind(this);
      this.onScroll = this.onScroll.bind(this);

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion || !this.groups.length) {
        for (const item of this.querySelectorAll('[data-kingpro-reveal]')) {
          item.classList.add('is-in');
        }

        return;
      }

      for (const group of this.groups) {
        this.groupState.set(group, false);
      }

      this.classList.add('is-ready');

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.onScrollCheck(), {
          threshold: [0, 0.2, 0.5, 0.8]
        });

        for (const group of this.groups) {
          this.observer.observe(group);
        }
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('touchmove', this.onScroll, { passive: true });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.onScrollCheck());
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('touchmove', this.onScroll);
    }

    onScroll() {
      if (this.scrollTick) return;

      this.scrollTick = true;

      requestAnimationFrame(() => {
        this.scrollTick = false;
        this.onScrollCheck();
      });
    }

    onScrollCheck() {
      for (const group of this.groups) {
        if (this.groupState.get(group)) continue;
        if (!this.isGroupInView(group)) continue;

        this.groupState.set(group, true);

        for (const item of group.querySelectorAll('[data-kingpro-reveal]')) {
          item.classList.add('is-in');
        }
      }
    }

    isGroupInView(group) {
      const rect = group.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;

      return rect.top < viewport * 0.78 && rect.bottom > viewport * 0.18;
    }
  }

  customElements.define('nodus-kingpro-anim', NodusKingproAnim);
}

if (!customElements.get('nodus-collection-search')) {
  class NodusCollectionSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-collection-search-input]');
      this.clearButton = this.querySelector('[data-collection-search-clear]');
      this.list = document.querySelector('[data-collection-search-list]');
      this.emptyState = document.querySelector('[data-collection-search-empty]');
      this.pagination = document.querySelector('.collection__pagination');
      this.cards = this.list
        ? Array.from(this.list.querySelectorAll('product-card[data-collection-search]'))
        : [];

      if (!this.input) return;

      this.boundInput = this.#onInput.bind(this);
      this.boundKeydown = this.#onKeydown.bind(this);
      this.boundClear = this.#onClear.bind(this);

      this.input.addEventListener('input', this.boundInput);
      this.input.addEventListener('keydown', this.boundKeydown);
      this.clearButton?.addEventListener('click', this.boundClear);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.boundInput);
      this.input?.removeEventListener('keydown', this.boundKeydown);
      this.clearButton?.removeEventListener('click', this.boundClear);
    }

    #onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    }

    #onClear() {
      this.input.value = '';
      this.input.focus();
      this.#filter('');
    }

    #onInput() {
      this.#filter(this.input.value);
    }

    #filter(rawQuery) {
      const query = rawQuery.trim().toLocaleLowerCase('tr');
      let visibleCount = 0;

      if (this.clearButton) {
        this.clearButton.hidden = query.length === 0;
      }

      for (const card of this.cards) {
        const terms = (card.getAttribute('data-collection-search') || '').toLocaleLowerCase('tr');
        const isMatch = query === '' || terms.includes(query);

        card.hidden = !isMatch;
        card.classList.toggle('is-collection-search-hidden', !isMatch);

        if (isMatch) {
          visibleCount += 1;
        }
      }

      if (this.emptyState) {
        this.emptyState.hidden = !(query !== '' && visibleCount === 0);
      }

      if (this.pagination) {
        this.pagination.hidden = query !== '';
      }

      if (this.list) {
        this.list.hidden = query !== '' && visibleCount === 0;
      }
    }
  }

  customElements.define('nodus-collection-search', NodusCollectionSearch);
}

if (!customElements.get('nodus-header-fit')) {
  class NodusHeaderFit extends HTMLElement {
    connectedCallback() {
      this.menu = this.querySelector('.header__main-nav .header__link-list');
      this.logo = this.querySelector('.header__logo');
      this.imageItem = this.querySelector('.header__menu-image-item');
      this.search = this.querySelector('.header__search-pill');
      this.iconList = this.querySelector('.header__secondary-nav .header__icon-list');
      this.header = this.closest('.header');
      this.shift = 0;

      if (!this.menu || !this.iconList) return;

      const headerDesktopMin = document.body.classList.contains('template-index') ? 1250 : 1350;

      this.desktopQuery = window.matchMedia(`(min-width: ${headerDesktopMin}px)`);
      this.boundUpdate = this.#schedule.bind(this);

      this.desktopQuery.addEventListener('change', this.boundUpdate);

      this.resizeObserver = new ResizeObserver(this.boundUpdate);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.menu);
      this.resizeObserver.observe(this.iconList);

      if (this.logo) this.resizeObserver.observe(this.logo);
      if (this.search) this.resizeObserver.observe(this.search);
      if (this.imageItem) this.resizeObserver.observe(this.imageItem);

      if (this.header) {
        this.headerObserver = new MutationObserver(this.boundUpdate);
        this.headerObserver.observe(this.header, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });

        const section = this.header.closest('.shopify-section');

        if (section) {
          this.sectionObserver = new MutationObserver(this.boundUpdate);
          this.sectionObserver.observe(section, {
            attributes: true,
            attributeFilter: ['class', 'style']
          });
        }
      }

      window.addEventListener('load', this.boundUpdate);
      document.fonts?.ready.then(this.boundUpdate);

      this.#schedule();
    }

    disconnectedCallback() {
      this.desktopQuery?.removeEventListener('change', this.boundUpdate);
      window.removeEventListener('load', this.boundUpdate);
      this.resizeObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.sectionObserver?.disconnect();

      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    #schedule() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.#update();
        });
      });
    }

    #isMeasurable() {
      const section = this.header?.closest('.shopify-section');

      if (section && getComputedStyle(section).visibility === 'hidden') return false;

      return this.offsetWidth > 0 && this.offsetHeight > 0;
    }

    #layoutX(visualX, isRtl) {
      return visualX + (isRtl ? -this.shift : this.shift);
    }

    #setShift(shift, isRtl) {
      if (this.shift === shift) return;

      this.shift = shift;
      this.menu.style.transform = shift
        ? (isRtl ? `translateX(${shift}px)` : `translateX(-${shift}px)`)
        : '';
    }

    #update() {
      if (!this.isConnected || !this.menu || !this.iconList || this.locked) return;

      if (!this.#isMeasurable()) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';

      if (!this.desktopQuery.matches) {
        this.#setShift(0, isRtl);
        return;
      }

      const edge = this.imageItem || this.menu;
      const edgeRect = edge.getBoundingClientRect();
      const obstacles = [this.iconList, this.search].filter(Boolean);
      let blockStart = isRtl ? -Infinity : Infinity;

      for (const obstacle of obstacles) {
        const rect = obstacle.getBoundingClientRect();

        if (rect.width === 0 || rect.height === 0) continue;

        blockStart = isRtl
          ? Math.max(blockStart, rect.right)
          : Math.min(blockStart, rect.left);
      }

      if (!Number.isFinite(blockStart) || edgeRect.width === 0) return;

      const gap = 16;
      const edgeLeft = this.#layoutX(edgeRect.left, isRtl);
      const edgeRight = this.#layoutX(edgeRect.right, isRtl);
      let overlap = isRtl
        ? blockStart + gap - edgeLeft
        : edgeRight + gap - blockStart;

      if (this.logo && this.logo.getBoundingClientRect().width > 0) {
        const logoRect = this.logo.getBoundingClientRect();
        const menuStart = this.menu.querySelector('li') || this.menu;
        const menuStartRect = menuStart.getBoundingClientRect();
        const room = isRtl
          ? logoRect.left - gap - this.#layoutX(menuStartRect.right, isRtl)
          : this.#layoutX(menuStartRect.left, isRtl) - gap - logoRect.right;

        overlap = Math.min(overlap, room);
      }

      this.locked = true;
      this.#setShift(overlap > 0 ? Math.ceil(overlap) : 0, isRtl);
      this.locked = false;
    }
  }

  customElements.define('nodus-header-fit', NodusHeaderFit);
}

if (!customElements.get('nodus-floating-button')) {
  class NodusFloatingButton extends HTMLElement {
    connectedCallback() {
      if (window.Shopify?.designMode) {
        this.#setVisible(true);
        return;
      }

      if (!this.hasAttribute('data-reveal-offset')) {
        this.#setVisible(true);
        return;
      }

      this.#setupScrollReveal();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      window.removeEventListener('scroll', this.boundOnScroll);

      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
    }

    #setupScrollReveal() {
      this.revealOffset = Number(this.getAttribute('data-reveal-offset')) || 400;
      this.boundOnScroll = this.#onScroll.bind(this);

      this.#updateFromScroll();
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    }

    #onScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.#updateFromScroll();
      });
    }

    #updateFromScroll() {
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;

      this.#setVisible(scrollY >= this.revealOffset);
    }

    #setVisible(isVisible) {
      this.classList.toggle('is-visible', isVisible);
      this.toggleAttribute('inert', !isVisible);

      if (isVisible) {
        this.removeAttribute('aria-hidden');
      } else {
        this.setAttribute('aria-hidden', 'true');
      }
    }
  }

  customElements.define('nodus-floating-button', NodusFloatingButton);
}

if (!customElements.get('nodus-search-quick-add')) {
  class NodusSearchQuickAdd extends HTMLButtonElement {
    constructor() {
      super();
      this.submitting = false;
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    async handleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.submitting || !this.dataset.variantId) return;

      this.submitting = true;
      this.setAttribute('aria-busy', 'true');

      const sectionsToBundle = ['variant-added'];

      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const formData = new FormData();

      formData.set('id', this.dataset.variantId);
      formData.set('quantity', '1');
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${window.Shopify.routes.root}variants/${this.dataset.variantId}`);

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const responseJson = await response.json();

        if (response.ok) {
          if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
            window.location.href = `${window.Shopify.routes.root}cart`;
            return;
          }

          const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();

          cartContent.sections = responseJson.sections;

          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: Object.prototype.hasOwnProperty.call(responseJson, 'items') ? responseJson.items : [responseJson],
              cart: cartContent
            }
          }));

          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson.description }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } finally {
        this.submitting = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('nodus-search-quick-add', NodusSearchQuickAdd, { extends: 'button' });
}

if (!customElements.get('nodus-search-suggestions')) {
  class NodusSearchSuggestions extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('search-drawer');
      this.onToggle = this.#onToggle.bind(this);
      this.drawer?.addEventListener('toggle', this.onToggle);
      this.#showRandom();
    }

    disconnectedCallback() {
      this.drawer?.removeEventListener('toggle', this.onToggle);
    }

    #onToggle(event) {
      if (event.target !== this.drawer || !this.drawer.hasAttribute('open')) return;

      this.#showRandom();
    }

    #showRandom() {
      const cards = [...this.querySelectorAll(':scope > .search-suggestion-card')];
      const visibleCount = Number.parseInt(this.dataset.visibleCount, 10) || 8;

      for (let index = cards.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        const current = cards[index];

        cards[index] = cards[swapIndex];
        cards[swapIndex] = current;
      }

      for (const [index, card] of cards.entries()) {
        card.hidden = index >= visibleCount;
        this.append(card);
      }
    }
  }

  customElements.define('nodus-search-suggestions', NodusSearchSuggestions);
}

if (!customElements.get('nodus-anchor-scroll')) {
  class NodusAnchorScroll extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    handleClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link || !this.contains(link)) return;

      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      const target = id ? document.getElementById(id) : null;
      if (!target) return;

      event.preventDefault();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', `#${id}`);
    }
  }

  customElements.define('nodus-anchor-scroll', NodusAnchorScroll);
}

const nodusLoadYouTubeApi = () => {
  if (window.YT?.Player) return Promise.resolve();

  window.nodusYouTubeApiPromise ||= new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };

    const script = document.createElement('script');

    script.src = 'https://www.youtube.com/iframe_api';
    document.head.append(script);
  });

  return window.nodusYouTubeApiPromise;
};

if (!customElements.get('nodus-video-card')) {
  class NodusVideoCard extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-video-trigger]');
      this.player = this.querySelector('[data-video-player]');
      this.poster = this.querySelector('[data-poster-fallback]');

      if (!this.trigger || !this.player) return;

      this.ready = false;
      this.pendingPlay = false;
      this.boundPlay = this.#play.bind(this);
      this.boundPosterError = this.#onPosterError.bind(this);

      this.trigger.addEventListener('click', this.boundPlay);
      this.poster?.addEventListener('error', this.boundPosterError);

      if (this.player.dataset.videoType === 'youtube') {
        this.#prepareYouTube();
      }
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.boundPlay);
      this.poster?.removeEventListener('error', this.boundPosterError);
      this.yt?.destroy();
    }

    #onPosterError() {
      const fallback = this.poster?.dataset.posterFallback;

      if (!fallback || this.poster.src === fallback) return;

      this.poster.src = fallback;
    }

    async #prepareYouTube() {
      await nodusLoadYouTubeApi();

      if (!this.isConnected) return;

      const mount = document.createElement('div');

      this.player.append(mount);

      this.yt = new YT.Player(mount, {
        videoId: this.player.dataset.videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          rel: 0,
          playsinline: 1,
          origin: window.location.origin
        },
        events: {
          onReady: () => {
            this.ready = true;

            const frame = this.player.querySelector('iframe');

            if (frame) {
              frame.style.width = '100%';
              frame.style.height = '100%';
            }

            if (this.pendingPlay) this.#startYouTube();
          }
        }
      });
    }

    #play() {
      const videoId = this.player.dataset.videoId;

      if (!videoId || this.classList.contains('is-playing')) return;

      this.#stopSiblings();
      this.classList.add('is-playing');
      this.trigger.hidden = true;

      if (this.player.dataset.videoType === 'vimeo') {
        this.#playVimeo(videoId);
        return;
      }

      if (this.ready) {
        this.#startYouTube();
        return;
      }

      this.pendingPlay = true;
    }

    #startYouTube() {
      this.pendingPlay = false;
      this.yt.unMute();
      this.yt.setVolume(100);
      this.yt.playVideo();
    }

    #playVimeo(videoId) {
      const iframe = document.createElement('iframe');

      iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      iframe.title = 'Reklam videosu';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      this.player.replaceChildren(iframe);
    }

    #stopSiblings() {
      const grid = this.closest('.reklam-video__grid');

      if (!grid) return;

      for (const card of grid.querySelectorAll('nodus-video-card.is-playing')) {
        if (card === this) continue;

        card.#reset();
      }
    }

    #reset() {
      this.classList.remove('is-playing');
      this.pendingPlay = false;
      this.yt?.pauseVideo();

      if (this.trigger) this.trigger.hidden = false;
    }
  }

  customElements.define('nodus-video-card', NodusVideoCard);
}

if (!customElements.get('nodus-announcement-panel')) {
  class NodusAnnouncementPanel extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-announcement-panel-toggle]');
      this.panel = this.querySelector('.announcement-link-panel');
      this.inDrawer = Boolean(this.closest('.announcement-bar__links--drawer'));
      this.boundToggle = this.#toggle.bind(this);
      this.boundOutside = this.#onOutside.bind(this);
      this.boundKey = this.#onKey.bind(this);
      this.boundDrawerHide = () => this.close();
      this.drawer = this.closest('navigation-drawer');

      this.button?.addEventListener('click', this.boundToggle);
      this.drawer?.addEventListener('dialog:after-hide', this.boundDrawerHide);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.boundToggle);
      this.drawer?.removeEventListener('dialog:after-hide', this.boundDrawerHide);
      this.#unbindDismiss();
    }

    #toggle(event) {
      event.preventDefault();
      event.stopPropagation();

      const willOpen = this.panel?.hasAttribute('hidden');

      document.querySelectorAll('nodus-announcement-panel').forEach((panel) => {
        if (panel !== this) panel.close();
      });

      if (willOpen) this.open();
      else this.close();
    }

    open() {
      if (!this.panel) return;

      this.panel.hidden = false;
      this.button?.setAttribute('aria-expanded', 'true');

      if (this.inDrawer) this.#placeAboveButton();

      document.addEventListener('click', this.boundOutside);
      document.addEventListener('keydown', this.boundKey, true);
    }

    close() {
      if (this.panel?.matches(':popover-open')) this.panel.hidePopover();

      this.panel?.setAttribute('hidden', '');
      this.button?.setAttribute('aria-expanded', 'false');
      this.#resetPlacement();
      this.#unbindDismiss();
    }

    #placeAboveButton() {
      const panel = this.panel;
      const button = this.button;

      if (!panel || !button) return;

      const usePopover = typeof panel.showPopover === 'function';

      if (usePopover) {
        panel.popover = 'manual';

        if (!panel.matches(':popover-open')) panel.showPopover();

        panel.style.position = 'fixed';
        panel.style.inset = 'auto';
        panel.style.margin = '0';
        panel.style.transform = 'none';
        panel.style.left = '0px';
        panel.style.top = '0px';
      }

      const margin = 12;
      const panelRect = panel.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      let left = buttonRect.left + buttonRect.width / 2 - panelRect.width / 2;

      left = Math.max(margin, Math.min(left, window.innerWidth - panelRect.width - margin));

      const top = Math.max(margin, buttonRect.top - panelRect.height - 10);

      if (usePopover) {
        panel.style.left = `${left}px`;
        panel.style.top = `${top}px`;
      } else {
        const parentRect = panel.offsetParent?.getBoundingClientRect() || { left: 0 };

        panel.style.transform = 'none';
        panel.style.left = `${left - parentRect.left}px`;
      }

      panel.style.setProperty('--announcement-arrow-left', `${buttonRect.left + buttonRect.width / 2 - left}px`);
    }

    #resetPlacement() {
      if (!this.panel) return;

      this.panel.style.position = '';
      this.panel.style.inset = '';
      this.panel.style.margin = '';
      this.panel.style.left = '';
      this.panel.style.top = '';
      this.panel.style.transform = '';
      this.panel.style.removeProperty('--announcement-arrow-left');
    }

    #unbindDismiss() {
      document.removeEventListener('click', this.boundOutside);
      document.removeEventListener('keydown', this.boundKey, true);
    }

    #onOutside(event) {
      if (!this.contains(event.target)) this.close();
    }

    #onKey(event) {
      if (event.key !== 'Escape') return;

      if (this.inDrawer) event.stopPropagation();

      this.close();
    }
  }

  customElements.define('nodus-announcement-panel', NodusAnnouncementPanel);
}

if (!customElements.get('nodus-wechat-modal')) {
  class NodusWechatModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.openButton = this.querySelector('[data-wechat-open]');
      this.closeButton = this.querySelector('[data-wechat-close]');
      this.boundOpen = this.#open.bind(this);
      this.boundClose = this.#close.bind(this);
      this.boundDialogClick = this.#onDialogClick.bind(this);
      this.boundDialogClose = this.#onDialogClose.bind(this);

      this.openButton?.addEventListener('click', this.boundOpen);
      this.closeButton?.addEventListener('click', this.boundClose);
      this.dialog?.addEventListener('click', this.boundDialogClick);
      this.dialog?.addEventListener('close', this.boundDialogClose);
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.boundOpen);
      this.closeButton?.removeEventListener('click', this.boundClose);
      this.dialog?.removeEventListener('click', this.boundDialogClick);
      this.dialog?.removeEventListener('close', this.boundDialogClose);
    }

    #open() {
      if (!this.dialog || this.dialog.open) return;

      this.dialog.showModal();
      this.openButton?.setAttribute('aria-expanded', 'true');
    }

    #close() {
      this.dialog?.close();
    }

    #onDialogClose() {
      this.openButton?.setAttribute('aria-expanded', 'false');
    }

    #onDialogClick(event) {
      if (event.target === this.dialog) this.#close();
    }
  }

  customElements.define('nodus-wechat-modal', NodusWechatModal);
}

if (!customElements.get('nodus-auto-rotate')) {
  class NodusAutoRotate extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector('scroll-carousel');
      this.seconds = Number(this.getAttribute('auto-rotate'));

      if (!this.carousel || !Number.isFinite(this.seconds) || this.seconds < 1) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.hovering = false;
      this.boundStart = this.#start.bind(this);
      this.boundStop = this.#stop.bind(this);
      this.boundVisibility = this.#onVisibility.bind(this);
      this.boundFocusOut = this.#onFocusOut.bind(this);

      this.addEventListener('pointerenter', this.boundStop);
      this.addEventListener('pointerleave', this.boundStart);
      this.addEventListener('focusin', this.boundStop);
      this.addEventListener('focusout', this.boundFocusOut);
      document.addEventListener('visibilitychange', this.boundVisibility);

      this.#start();
    }

    disconnectedCallback() {
      this.#clearTimer();
      this.removeEventListener('pointerenter', this.boundStop);
      this.removeEventListener('pointerleave', this.boundStart);
      this.removeEventListener('focusin', this.boundStop);
      this.removeEventListener('focusout', this.boundFocusOut);
      document.removeEventListener('visibilitychange', this.boundVisibility);
    }

    #start() {
      this.hovering = false;

      if (document.hidden || this.timer) return;

      this.timer = window.setInterval(() => this.#advance(), this.seconds * 1000);
    }

    #stop() {
      this.hovering = true;
      this.#clearTimer();
    }

    #onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.#start();
    }

    #onVisibility() {
      if (document.hidden) {
        this.#clearTimer();
        return;
      }

      if (!this.hovering) this.#start();
    }

    #clearTimer() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    #advance() {
      const carousel = this.carousel;

      if (!carousel || typeof carousel.next !== 'function' || typeof carousel.select !== 'function') return;

      const maxScroll = carousel.scrollWidth - carousel.clientWidth;

      if (maxScroll <= 2 || carousel.totalPages <= 1) return;

      const atEnd = Math.abs(carousel.scrollLeft) >= maxScroll - 4 || carousel.selectedIndexAmongVisible >= carousel.totalPages - 1;

      if (atEnd) {
        carousel.select(0, { animate: true, force: true });
        return;
      }

      carousel.next();
    }
  }

  customElements.define('nodus-auto-rotate', NodusAutoRotate);
}

const updateCollectionListFade = (slider) => {
  const carousel = slider.querySelector('scroll-carousel');

  if (!carousel) return;

  const maxScroll = carousel.scrollWidth - carousel.clientWidth;
  const hasOverflow = maxScroll > 2;
  const atEnd = !hasOverflow || carousel.scrollLeft >= maxScroll - 2;

  slider.classList.toggle('has-overflow', hasOverflow);
  slider.classList.toggle('is-scrolled-end', atEnd);
};

const setupCollectionListFade = (root = document) => {
  for (const slider of root.querySelectorAll('.collection-list-slider')) {
    if (slider.dataset.fadeReady === 'true') {
      updateCollectionListFade(slider);
      continue;
    }

    const carousel = slider.querySelector('scroll-carousel');

    if (!carousel) continue;

    slider.dataset.fadeReady = 'true';

    const update = () => updateCollectionListFade(slider);

    carousel.addEventListener('scroll', update, { passive: true });
    carousel.addEventListener('scrollend', update);

    if (window.ResizeObserver) {
      const observer = new ResizeObserver(update);

      observer.observe(carousel);
    }

    update();
  }
};

setupCollectionListFade();

const applyCartDiscount = (field) => {
  const input = field?.querySelector('.input');

  if (!input || input.value.trim() === '') return;

  input.dispatchEvent(new Event('change', { bubbles: true }));
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-discount-apply]');

  if (!button) return;

  event.preventDefault();
  applyCartDiscount(button.closest('cart-discount-field'));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;

  const input = event.target.closest?.('cart-discount-field .input');

  if (!input) return;

  event.preventDefault();
  applyCartDiscount(input.closest('cart-discount-field'));
});

document.addEventListener('shopify:section:load', (event) => {
  setupCollectionListFade(event.target);
  setupNodusFitRows(event.target);
});

const matchNodusPurchaseHeights = (row) => {
  const controls = [...row.querySelectorAll('.quantity-selector, .button')];

  for (const control of controls) {
    control.style.height = '';
    control.style.minHeight = '';
  }

  let height = 0;

  for (const control of controls) {
    if (control.closest('.block-label')) continue;

    height = Math.max(height, control.offsetHeight);
  }

  if (!height) return;

  for (const control of controls) {
    control.style.height = `${height}px`;
    control.style.minHeight = `${height}px`;
  }
};

const fitNodusPurchaseCell = (cell) => {
  const content = cell.firstElementChild;

  if (!content) return;

  content.style.transform = '';
  content.style.transformOrigin = 'left top';
  cell.style.height = '';

  const available = cell.clientWidth;
  const needed = content.scrollWidth;

  if (needed <= available + 1 || available <= 0) return;

  const scale = available / needed;

  content.style.transform = `scale(${scale})`;
  cell.style.height = `${content.offsetHeight * scale}px`;
};

const fitNodusMetaRow = (row) => {
  const track = row.querySelector('.nodus-fit-row__track');

  if (!track) return;

  track.style.transform = '';
  row.style.height = '';

  const available = row.clientWidth;
  const needed = track.scrollWidth;

  if (needed <= available + 1 || available <= 0) return;

  const scale = available / needed;

  track.style.transform = `scale(${scale})`;
  row.style.height = `${track.offsetHeight * scale}px`;
};

const fitNodusRow = (row) => {
  if (row.dataset.fitting === 'true') return;

  row.dataset.fitting = 'true';

  if (row.classList.contains('nodus-purchase-row')) {
    matchNodusPurchaseHeights(row);

    for (const cell of row.children) {
      fitNodusPurchaseCell(cell);
    }
  } else {
    fitNodusMetaRow(row);
  }

  row.dataset.fitting = 'false';
};

const setupNodusFitRows = (root = document) => {
  for (const row of root.querySelectorAll('.nodus-purchase-row, .nodus-pdp-meta-row')) {
    if (row.dataset.fitReady !== 'true') {
      row.dataset.fitReady = 'true';

      if (window.ResizeObserver) {
        let lastWidth = 0;
        let lastNeeded = 0;
        const observer = new ResizeObserver(() => {
          const needed = row.querySelector('.nodus-fit-row__track')?.scrollWidth || row.scrollWidth;

          if (row.clientWidth === lastWidth && needed === lastNeeded) return;

          lastWidth = row.clientWidth;
          lastNeeded = needed;
          fitNodusRow(row);
        });

        observer.observe(row);

        const track = row.querySelector('.nodus-fit-row__track');

        if (track) observer.observe(track);
      }
    }

    fitNodusRow(row);
  }
};

setupNodusFitRows();
window.addEventListener('load', () => setupNodusFitRows());
document.addEventListener('product:rerender', () => {
  requestAnimationFrame(() => setupNodusFitRows());
}, true);

if (!customElements.get('nodus-announcement-links')) {
  class NodusAnnouncementLinks extends HTMLElement {
    connectedCallback() {
      this.mount();
    }

    mount() {
      const template = document.getElementById('nodus-announcement-links-template');
      const hasLinks = template?.content?.querySelector('li');

      if (!hasLinks) {
        this.hidden = true;
        this.replaceChildren();
        return;
      }

      this.hidden = false;
      this.replaceChildren(template.content.cloneNode(true));
    }
  }

  customElements.define('nodus-announcement-links', NodusAnnouncementLinks);
}

document.addEventListener('shopify:section:load', () => {
  for (const list of document.querySelectorAll('nodus-announcement-links')) {
    list.mount();
  }
});

const mobileChatButtonCss = `@media screen and (max-width: 699px) {
  [class*="chat-icon-container"] {
    bottom: 10px !important;
    top: auto !important;
  }

  :is(button, a, [role="button"]):has([class*="chat-icon-icon"]),
  [class*="chat-icon-icon"]:is(button, a, [role="button"]),
  [class*="chat-icon-icon"]:not(:is(button, a, [role="button"], button *, a *, [role="button"] *)) {
    scale: 0.8 !important;
  }
}`;

const installMobileChatButtonScale = () => {
  const roots = new Set([document]);
  collectLayerRoots(document, roots);

  for (const root of roots) {
    if (!(root instanceof ShadowRoot)) continue;
    if (root.querySelector('#nodus-mobile-chat-button')) continue;
    if (!root.querySelector('[class*="chat-icon-icon"]')) continue;

    const style = document.createElement('style');
    style.id = 'nodus-mobile-chat-button';
    style.textContent = mobileChatButtonCss;
    root.appendChild(style);
  }
};

let mobileChatButtonFrame = 0;

const scheduleMobileChatButtonScale = () => {
  if (mobileChatButtonFrame) return;

  mobileChatButtonFrame = window.requestAnimationFrame(() => {
    mobileChatButtonFrame = 0;
    installMobileChatButtonScale();
  });
};

scheduleMobileChatButtonScale();
window.addEventListener('load', scheduleMobileChatButtonScale);

const mobileChatButtonObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.shadowRoot || node.querySelector?.('[class*="chat-icon"], [class*="shopify-chat"]')) {
        scheduleMobileChatButtonScale();
        return;
      }
    }
  }
});

mobileChatButtonObserver.observe(document.documentElement, { childList: true, subtree: true });

const hiddenChatLayers = new Set();
const savedChatStyles = new WeakMap();
const chatLayerSelector = '[class*="chat-icon-container"], [class*="shopify-chat"], [id*="ShopifyChat" i], [id*="shopify-chat" i], iframe[id*="chat" i], iframe[name*="chat" i], iframe[title*="chat" i]';

const sidebarMenu = () => document.getElementById('header-sidebar-menu');

const loginLayerSelector = '.oxiblocker, .oximodal, .oxi-ad-modal, .remodal-overlay, .remodal-wrapper, #oxipopup_login, #oxipopup_register, #oxipopup_forgot_password, #oxi-ad-modal';

const isLoginLayer = (element) => {
  if (!(element instanceof Element)) return false;
  if (element.matches(loginLayerSelector)) return true;

  return Boolean(element.querySelector(loginLayerSelector));
};

const belongsToSidebarMenu = (element) => {
  const drawer = sidebarMenu();

  if (!drawer) return false;

  let current = element;

  while (current && current !== document.body && current !== document.documentElement) {
    if (current === drawer) return true;

    if (current.parentElement) {
      current = current.parentElement;
      continue;
    }

    const root = current.getRootNode();
    current = root instanceof ShadowRoot ? root.host : null;
  }

  return false;
};

const collectLayerRoots = (root, found) => {
  if (!root?.querySelectorAll) return;

  for (const element of root.querySelectorAll('*')) {
    if (!element.shadowRoot) continue;

    found.add(element.shadowRoot);
    collectLayerRoots(element.shadowRoot, found);
  }
};

const restoreHiddenLayer = (element) => {
  const saved = savedChatStyles.get(element);

  if (!saved) return;

  if (saved.visibility) element.style.setProperty('visibility', saved.visibility, saved.visibilityPriority);
  else element.style.removeProperty('visibility');

  if (saved.opacity) element.style.setProperty('opacity', saved.opacity, saved.opacityPriority);
  else element.style.removeProperty('opacity');

  if (saved.pointerEvents) element.style.setProperty('pointer-events', saved.pointerEvents, saved.pointerEventsPriority);
  else element.style.removeProperty('pointer-events');

  savedChatStyles.delete(element);
  hiddenChatLayers.delete(element);
};

const hideChatLayer = (element) => {
  if (!(element instanceof Element) || element === document.body || element === document.documentElement) return;
  if (isLoginLayer(element)) {
    restoreHiddenLayer(element);
    return;
  }
  if (belongsToSidebarMenu(element) || element.contains(sidebarMenu())) return;
  if (element.contains(document.getElementById('main'))) return;

  if (!savedChatStyles.has(element)) {
    savedChatStyles.set(element, {
      visibility: element.style.getPropertyValue('visibility'),
      visibilityPriority: element.style.getPropertyPriority('visibility'),
      opacity: element.style.getPropertyValue('opacity'),
      opacityPriority: element.style.getPropertyPriority('opacity'),
      pointerEvents: element.style.getPropertyValue('pointer-events'),
      pointerEventsPriority: element.style.getPropertyPriority('pointer-events')
    });
    hiddenChatLayers.add(element);
  }

  if (element.style.getPropertyValue('visibility') !== 'hidden' || element.style.getPropertyPriority('visibility') !== 'important') {
    element.style.setProperty('visibility', 'hidden', 'important');
    element.style.setProperty('opacity', '0', 'important');
    element.style.setProperty('pointer-events', 'none', 'important');
  }

  if (typeof element.hidePopover === 'function' && element.matches(':popover-open')) {
    element.hidePopover();
  }
};

const chatHost = (element) => {
  let current = element;
  let host = element;

  while (current && current !== document.body && current !== document.documentElement) {
    host = current;

    if (current.parentElement) {
      current = current.parentElement;
      continue;
    }

    const root = current.getRootNode();
    current = root instanceof ShadowRoot ? root.host : null;
  }

  return host;
};

const hideChatWhileMenuOpen = () => {
  const drawer = sidebarMenu();

  if (!drawer?.hasAttribute('open')) return;

  const roots = new Set([document]);
  collectLayerRoots(document, roots);

  for (const child of [...document.body.children, ...document.documentElement.children]) {
    if (!(child instanceof Element) || belongsToSidebarMenu(child)) continue;
    if (child.matches('.popover, x-popover') || isLoginLayer(child)) continue;

    const style = window.getComputedStyle(child);
    const zIndex = Number.parseInt(style.zIndex, 10);
    const floatsAbove = (style.position === 'fixed' || style.position === 'sticky') && Number.isFinite(zIndex) && zIndex >= 1000;

    if (floatsAbove) hideChatLayer(child);
  }

  for (const root of roots) {
    for (const element of root.querySelectorAll(chatLayerSelector)) {
      hideChatLayer(chatHost(element));
    }
  }

  for (const element of hiddenChatLayers) hideChatLayer(element);
};

const restoreChatLayers = () => {
  for (const element of hiddenChatLayers) restoreHiddenLayer(element);
};

let chatLockObserver = null;
let chatLockTimer = 0;

const lockChatBelowSidebar = () => {
  document.documentElement.classList.add('nodus-menu-open');
  hideChatWhileMenuOpen();

  if (!chatLockObserver) {
    chatLockObserver = new MutationObserver(() => {
      if (!sidebarMenu()?.hasAttribute('open')) return;

      hideChatWhileMenuOpen();
    });
    chatLockObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  if (!chatLockTimer) chatLockTimer = window.setInterval(hideChatWhileMenuOpen, 200);
};

const unlockChatBelowSidebar = () => {
  chatLockObserver?.disconnect();
  chatLockObserver = null;
  window.clearInterval(chatLockTimer);
  chatLockTimer = 0;
  document.documentElement.classList.remove('nodus-menu-open');
  restoreChatLayers();
};

document.addEventListener('click', (event) => {
  const drawer = sidebarMenu();

  if (!drawer?.open) return;

  const account = event.composedPath().find((node) => node instanceof Element && node.tagName === 'SHOPIFY-ACCOUNT');

  if (!account || !drawer.contains(account)) return;

  unlockChatBelowSidebar();
  drawer.hide();
}, true);

document.addEventListener('dialog:after-show', (event) => {
  if (event.target?.id !== 'header-sidebar-menu') return;

  lockChatBelowSidebar();
});

document.addEventListener('dialog:after-hide', (event) => {
  if (event.target?.id !== 'header-sidebar-menu') return;

  unlockChatBelowSidebar();
});

const slideshowTouch = new WeakMap();

document.addEventListener('pointerdown', (event) => {
  if (!window.matchMedia('(max-width: 699px)').matches) return;

  const carousel = event.target.closest('slideshow-carousel');

  if (!carousel) return;
  if (event.target.closest('a:not(.slideshow__banner-link-mobile), button, .button')) return;

  slideshowTouch.set(carousel, {
    x: event.clientX,
    y: event.clientY,
    pointerId: event.pointerId
  });
});

document.addEventListener('pointerup', (event) => {
  const carousel = event.target.closest('slideshow-carousel');
  const start = carousel ? slideshowTouch.get(carousel) : null;

  if (!start || start.pointerId !== event.pointerId) return;

  slideshowTouch.delete(carousel);

  const distanceX = Math.abs(event.clientX - start.x);
  const distanceY = Math.abs(event.clientY - start.y);

  if (distanceX > 12 || distanceY > 12) return;

  const link = carousel.querySelector('.slideshow__slide.is-selected .slideshow__banner-link-mobile');

  if (!link?.href) return;

  window.location.assign(link.href);
});

const anchorSortPopover = (popover) => {
  if (!(popover instanceof HTMLElement) || popover.dataset.sortDropdown === 'true') return;

  popover.dataset.sortDropdown = 'true';

  Object.defineProperty(popover, 'shouldAppendToBody', {
    configurable: true,
    get() {
      return false;
    }
  });

  Object.defineProperty(popover, 'shouldLock', {
    configurable: true,
    get() {
      return false;
    }
  });

  popover._showTransition = function showSortDropdown() {
    const content = this.shadowRoot?.querySelector('[part="content"]');
    const spacingBlock = 'var(--popover-anchor-block-spacing)';
    const spacingInline = 'var(--popover-anchor-inline-spacing)';

    this.style.display = 'block';
    this.style.position = 'absolute';
    this.style.insetInlineStart = this.anchor.horizontal === 'start' ? spacingInline : '';
    this.style.insetInlineEnd = this.anchor.horizontal === 'end' ? spacingInline : '';
    this.style.insetBlockEnd = '';
    this.style.insetBlockStart = `calc(100% + ${spacingBlock})`;
    this.style.opacity = '1';
    this.style.visibility = 'visible';

    if (content) {
      content.style.clipPath = 'none';
    }

    return Promise.resolve();
  };

  popover._hideTransition = function hideSortDropdown() {
    this.style.opacity = '0';
    this.style.visibility = 'hidden';

    return Promise.resolve().then(() => {
      this.style.display = 'none';
    });
  };
};

const anchorSortPopovers = (root = document) => {
  root.querySelectorAll('.sort-by-facet__popover').forEach(anchorSortPopover);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => anchorSortPopovers());
} else {
  anchorSortPopovers();
}

document.addEventListener('shopify:section:load', (event) => {
  anchorSortPopovers(event.target);
});

const sortPopoverObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      if (node.matches('.sort-by-facet__popover')) anchorSortPopover(node);

      node.querySelectorAll('.sort-by-facet__popover').forEach(anchorSortPopover);
    });
  });
});

sortPopoverObserver.observe(document.documentElement, { childList: true, subtree: true });

const wishlistMobileQuery = window.matchMedia('(max-width: 699px)');

const syncWishlistPlacement = () => {
  const headerSlot = document.querySelector('.header__icon-list .wishlistr-header-slot');
  const drawer = document.querySelector('#header-sidebar-menu');
  const drawerSlot = drawer?.querySelector('.navigation-drawer__favorites');

  if (!headerSlot || !drawer || !drawerSlot) return;

  const headerButton = headerSlot.querySelector('.wishlistr-header-btn');
  const drawerButton = drawerSlot.querySelector('.wishlistr-header-btn');
  const mobileMenuOpen = wishlistMobileQuery.matches && drawer.hasAttribute('open');

  if (mobileMenuOpen && !drawerButton && headerButton) drawerSlot.appendChild(headerButton);
  if (!mobileMenuOpen && !headerButton && drawerButton) headerSlot.appendChild(drawerButton);

  const menuButton = drawerSlot.querySelector('.wishlistr-header-btn');
  const menuLabel = drawerSlot.dataset.favoritesLabel;

  if (menuButton && menuLabel) menuButton.setAttribute('aria-label', menuLabel);

  document.querySelectorAll('.wishlistr-header-btn').forEach((button) => {
    if (!wishlistMobileQuery.matches) {
      button.style.removeProperty('display');
      return;
    }

    const visible = mobileMenuOpen && drawerSlot.contains(button);
    button.style.setProperty('display', visible ? 'grid' : 'none', 'important');
  });
};

let wishlistPlacementFrame = 0;

const scheduleWishlistPlacement = () => {
  if (wishlistPlacementFrame) return;

  wishlistPlacementFrame = window.requestAnimationFrame(() => {
    wishlistPlacementFrame = 0;
    syncWishlistPlacement();
  });
};

scheduleWishlistPlacement();
wishlistMobileQuery.addEventListener('change', scheduleWishlistPlacement);
document.addEventListener('shopify:section:load', scheduleWishlistPlacement);

const wishlistPlacementObserver = new MutationObserver(scheduleWishlistPlacement);

wishlistPlacementObserver.observe(document.documentElement, { childList: true, subtree: true });

const headerSidebarMenu = document.querySelector('#header-sidebar-menu');

if (headerSidebarMenu) {
  new MutationObserver(scheduleWishlistPlacement).observe(headerSidebarMenu, {
    attributes: true,
    attributeFilter: ['open']
  });
}
